import React from 'react';
import PropTypes from 'prop-types';
import cN from 'classnames';

import Button from 'scripts/components/Button';
import MarkDown from 'scripts/components/MarkDown';
import {_} from 'utils/i18n';

import LessonBlock from 'scripts/frontend/lessons/components/LessonBlock';

import 'styles/frontend/lessons.scss';

import LessonContainer from './LessonContainer';

const missingTexts = {
    meditation: _('Please fully complete the meditation.'),
    default: _('Please answer the questions marked red.'),
};

const notifyOptionalMissingTexts = {
    meditation: _('Please consider fully complete the meditation. To skip the mediation click again on the button.'),
    default: _('Please consider answer the questions marked red. To skip the question click again on the button.'),
};

const getBlockProps = (answers, blocks, block) => {
    if (block.type === 'group') {
        const subBlocks = blocks.filter(subBlock => subBlock.group === block.id);
        const blockAnswers = subBlocks.map(subBlock => answers[subBlock.id] || {});

        return {answer: blockAnswers, extra: {blocks: subBlocks}};
    }

    const answer = answers[block.id] || {};
    return {answer, extra: {}};
};

const getProgress = (blocks, block) => {
    const pages = blocks.filter(b => !(b.group || b.type === 'jump')).map(b => b.id);
    const currentPage = pages.indexOf(block.id) + 1;

    return {pages: pages.length, currentPage};
};

const getMissingText = block => missingTexts[block.type] || missingTexts.default;
const getNotifyOptionalMissingText = block => notifyOptionalMissingTexts[block.type] || notifyOptionalMissingTexts.default;

const Lesson = ({blocks, index, answers, missingInfo, notifyOptionalMissing, updateAnswer, onBack, onContinue, LessonBlockComponent,
                    isRepeatable, isAbortable, renderAbortModal}) => {
    const prevBlock = index > 0 ? blocks[index - 1] : null;
    const block = blocks[index];
    const {answer, extra} = getBlockProps(answers, blocks, block);
    const {pages, currentPage} = getProgress(blocks, block);
    const missingText = getMissingText(block);
    const notifyOptionalMissingText = getNotifyOptionalMissingText(block);
    // Hide controls if media block has autoplay enabled
    const hideControls = block.type === 'media' && block.config.autoplay;

    return (
        <LessonContainer>
            {isAbortable && renderAbortModal()}
            <div>
                <div className="bg-grey-light rounded-sm p-4 mb-2 text-center">
                    <MarkDown text={block.intro} />
                    <LessonBlockComponent
                        key={block.id}
                        {...block}
                        answer={answer}
                        missingInfo={missingInfo}
                        notifyOptionalMissing={notifyOptionalMissing}
                        updateAnswer={updateAnswer}
                        {...extra}
                        onContinue={onContinue}
                    />
                    <div className="w-full text-center">
                        <span>{`${currentPage}/${pages}`}</span>
                    </div>
                </div>
                <div className={cN('flex', 'flex-row-reverse', 'justify-between', 'space-x-reverse-4', (hideControls ? 'hidden' : ''))}>
                    <Button className="rounded-sm w-20 h-16 shadow-none flex items-center justify-center" onClick={onContinue}>
                        <i className="material-icons text-white text-5xl">arrow_forward</i>
                    </Button>
                    {missingInfo && <p className="text-center text-red">{missingText}</p>}
                    {notifyOptionalMissing && <p className="text-center text-red">{notifyOptionalMissingText}</p>}
                    {isRepeatable(prevBlock) && index !== 0 && (
                        <Button
                            className="rounded-sm w-20 h-16 shadow-none flex items-center justify-center bg-lightyellow"
                            onClick={onBack}
                            disabled={index === 0}>
                            <i className="material-icons text-yellow text-5xl">arrow_back</i>
                        </Button>
                    )}
                </div>
            </div>
        </LessonContainer>
    );
};

Lesson.propTypes = {
    blocks: PropTypes.arrayOf(PropTypes.any).isRequired,
    index: PropTypes.number.isRequired,
    answers: PropTypes.objectOf(PropTypes.any).isRequired,
    updateAnswer: PropTypes.func,
    onBack: PropTypes.func,
    onContinue: PropTypes.func,
    isRepeatable: PropTypes.func,
    isAbortable: PropTypes.bool,
    renderAbortModal: PropTypes.func,
    LessonBlockComponent: PropTypes.func,
};

const NOOP = () => {};

Lesson.defaultProps = {
    updateAnswer: NOOP,
    onBack: NOOP,
    onContinue: NOOP,
    isRepeatable: NOOP,
    isAbortable: true,
    renderAbortModal: NOOP,
    LessonBlockComponent: LessonBlock,
};

export default Lesson;



// WEBPACK FOOTER //
// ./scripts/frontend/lessons/components/Lesson/Lesson.js